import React from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

interface Location {
    id: number;
    name: string;
}

interface RelayDevice {
    id: number;
    name: string;
}

interface Camera {
    id: number;
    name: string;
    role: string;
}

interface GateModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (e: React.FormEvent) => void;
    formData: {
        id?: number;
        locationId: string;
        name: string;
        description: string;
        relayDeviceId: string;
        relayChannelNumber: string;
        entryCameraId: string;
        exitCameraId: string;
    };
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
    locations: Location[];
    relays: RelayDevice[];
    entryCameras: Camera[];
    exitCameras: Camera[];
    loading: boolean;
    isEdit: boolean;
}

export const GateModal: React.FC<GateModalProps> = ({
    show,
    onHide,
    onSubmit,
    formData,
    handleInputChange,
    locations,
    relays,
    entryCameras,
    exitCameras,
    loading,
    isEdit
}) => {
    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title>{isEdit ? 'Edit Gate' : 'Add New Gate'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={onSubmit}>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="gateLocation">
                                <Form.Label>Location <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    name="locationId"
                                    value={formData.locationId}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select Location</option>
                                    {locations.map(loc => (
                                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="gateName">
                                <Form.Label>Gate Name <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="e.g. Main Entrance Gate"
                                    required
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <div className="mb-3">
                        <Form.Group controlId="gateDesc">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Purpose or notes about this gate"
                            />
                        </Form.Group>
                    </div>

                    <hr className="my-4" />
                    <h6 className="text-primary mb-3"><i className="ti ti-device-hub me-1"></i> Control Hardware (Relay)</h6>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="gateRelay">
                                <Form.Label>Relay Device <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    name="relayDeviceId"
                                    value={formData.relayDeviceId}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Select Relay</option>
                                    {relays.map(r => (
                                        <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="gateChannel">
                                <Form.Label>Channel Number <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="number"
                                    name="relayChannelNumber"
                                    value={formData.relayChannelNumber}
                                    onChange={handleInputChange}
                                    placeholder="e.g. 0, 1, 2..."
                                    required
                                    min="0"
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <hr className="my-4" />
                    <h6 className="text-info mb-3"><i className="ti ti-camera me-1"></i> Monitoring Hardware (Cameras)</h6>

                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="gateEntryCam">
                                <Form.Label>Entry Camera (ANPR)</Form.Label>
                                <Form.Select
                                    name="entryCameraId"
                                    value={formData.entryCameraId}
                                    onChange={handleInputChange}
                                >
                                    <option value="">None</option>
                                    {entryCameras.map(cam => (
                                        <option key={cam.id} value={cam.id}>{cam.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>
                        <div className="col-md-6 mb-3">
                            <Form.Group controlId="gateExitCam">
                                <Form.Label>Exit Camera (ANPR)</Form.Label>
                                <Form.Select
                                    name="exitCameraId"
                                    value={formData.exitCameraId}
                                    onChange={handleInputChange}
                                >
                                    <option value="">None</option>
                                    {exitCameras.map(cam => (
                                        <option key={cam.id} value={cam.id}>{cam.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>
                    </div>

                    <div className="text-end mt-4">
                        <Button variant="light" onClick={onHide} className="me-2" disabled={loading}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Processing...' : isEdit ? 'Update Gate' : 'Create Gate'}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

interface Gate {
    id: number;
    location?: { id: number; name: string };
    name: string;
    description: string;
    relayDevice?: { id: number; name: string };
    relayChannelNumber: number;
    entryCamera?: { id: number; name: string };
    exitCamera?: { id: number; name: string };
}

interface ViewGateModalProps {
    show: boolean;
    onHide: () => void;
    gate: Gate | null;
}

export const ViewGateModal: React.FC<ViewGateModalProps> = ({
    show,
    onHide,
    gate
}) => {
    if (!gate) return null;
    
    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton className="border-0">
                <Modal.Title>Gate Profile Details</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <div className="row g-4">
                    <div className="col-12 text-center mb-2">
                        <div className="bg-primary-subtle p-3 rounded-pill d-inline-block px-5">
                            <h4 className="mb-0 text-primary">{gate.name}</h4>
                            <span className="text-muted small">ID: {gate.id}</span>
                        </div>
                    </div>
                    
                    <div className="col-md-6">
                        <div className="p-3 border rounded h-100 bg-light-subtle">
                            <h6 className="text-uppercase fw-bold text-muted small mb-3">General Information</h6>
                            <div className="mb-2">
                                <label className="text-muted small d-block">Location</label>
                                <span className="fw-medium">{gate.location?.name || 'N/A'}</span>
                            </div>
                            <div>
                                <label className="text-muted small d-block">Description</label>
                                <p className="mb-0 small">{gate.description || 'No description provided.'}</p>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="p-3 border rounded h-100 border-primary-subtle bg-primary-subtle">
                            <h6 className="text-uppercase fw-bold text-primary small mb-3"><i className="ti ti-device-hub me-1"></i> Control Logic</h6>
                            <div className="mb-2">
                                <label className="text-muted small d-block">Relay Device</label>
                                <span className="fw-bold">{gate.relayDevice?.name || 'Not Linked'}</span>
                            </div>
                            <div>
                                <label className="text-muted small d-block">Trigger Channel</label>
                                <span className="badge bg-primary text-white px-3">Channel {gate.relayChannelNumber}</span>
                            </div>
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="p-3 border rounded bg-info-subtle border-info-subtle">
                            <h6 className="text-uppercase fw-bold text-info small mb-3"><i className="ti ti-camera me-1"></i> Monitoring Suite</h6>
                            <div className="row">
                                <div className="col-md-6">
                                    <label className="text-muted small d-block">Entry Monitoring (ANPR)</label>
                                    {gate.entryCamera ? (
                                        <div className="d-flex align-items-center">
                                            <i className="ti ti-arrow-right-circle text-success me-2"></i>
                                            <span className="fw-medium">{gate.entryCamera.name}</span>
                                        </div>
                                    ) : <span className="text-muted small italic">Not configured</span>}
                                </div>
                                <div className="col-md-6 border-start border-info">
                                    <label className="text-muted small d-block">Exit Monitoring (ANPR)</label>
                                    {gate.exitCamera ? (
                                        <div className="d-flex align-items-center">
                                            <i className="ti ti-arrow-left-circle text-warning me-2"></i>
                                            <span className="fw-medium">{gate.exitCamera.name}</span>
                                        </div>
                                    ) : <span className="text-muted small italic">Not configured</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="secondary" onClick={onHide}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
};

interface DeleteModalProps {
    show: boolean;
    onHide: () => void;
    onConfirm: () => void;
    gateName: string;
    loading: boolean;
}

export const DeleteGateModal: React.FC<DeleteModalProps> = ({
    show,
    onHide,
    onConfirm,
    gateName,
    loading
}) => {
    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title>Delete Gate</Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center">
                <div className="text-danger mb-4">
                    <i className="ti ti-trash fs-1" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5>Confirm Removal</h5>
                <p className="text-muted">Are you sure you want to remove the <strong>{gateName}</strong>? Hardware configurations will be unlinked.</p>
            </Modal.Body>
            <Modal.Footer className="border-0 justify-content-center">
                <Button variant="light" onClick={onHide} disabled={loading}>
                    Cancel
                </Button>
                <Button variant="danger" onClick={onConfirm} disabled={loading}>
                    {loading ? 'Deleting...' : 'Yes, Delete It'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
